// ============================================
// Auth Upgrade Service
//
// Upgrades anonymous Firebase Auth to email/password
// using linkWithCredential(). Preserves the existing UID
// so all Firestore data (journeys, profiles, orders)
// stays linked to the same user.
//
// Also handles sign-in for returning patients and
// migration of journey data when linking.
//
// Phase 6 spec: linkWithCredential() to upgrade
// anonymous sessions without losing data.
// ============================================

import {
  EmailAuthProvider,
  linkWithCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { doc, setDoc, Timestamp, getDocs, collection, query, where, orderBy } from 'firebase/firestore';
import { auth, db, resetAuthUid } from '../firebase';
import { onAuthUpgrade, onLogin } from '../sessionService';
import { C_PATIENT_JOURNEYS, C_USER_PROFILES } from '../../constants/collections';

// ── Types ──

export interface AuthUpgradeResult {
  success: boolean;
  uid: string;
  email: string;
  isNewAccount: boolean;
  error?: string;
}

export interface PatientAuthState {
  uid: string | null;
  email: string | null;
  isAnonymous: boolean;
  isAuthenticated: boolean;
}

// ── Upgrade anonymous to email/password ──

/**
 * Upgrade the current anonymous user to email/password auth.
 * Uses linkWithCredential() so the UID stays the same and
 * all existing Firestore data remains linked.
 *
 * Fire-and-forget profile update -- writes email to user_profiles
 * but does not block on it.
 */
export async function upgradeAnonymousToEmail(
  email: string,
  password: string,
): Promise<AuthUpgradeResult> {
  const user = auth.currentUser;

  if (!user) {
    return { success: false, uid: '', email, isNewAccount: false, error: 'No authenticated user found' };
  }

  // If user is already linked with email, return success
  if (!user.isAnonymous && user.email) {
    return { success: true, uid: user.uid, email: user.email, isNewAccount: false };
  }

  try {
    const credential = EmailAuthProvider.credential(email, password);
    const result = await linkWithCredential(user, credential);

    // Fire-and-forget: persist email to user_profiles
    persistEmailToProfile(result.user.uid, email).catch((err) =>
      console.warn('[AuthUpgrade] Profile email write failed:', err),
    );

    // Rotate session on anonymous → authenticated upgrade
    onAuthUpgrade();

    return { success: true, uid: result.user.uid, email, isNewAccount: true };
  } catch (err: unknown) {
    const code = (err as { code?: string }).code;
    const message = (err as Error).message;

    // If the email is already in use, the patient may already have an account
    if (code === 'auth/email-already-in-use') {
      return {
        success: false,
        uid: user.uid,
        email,
        isNewAccount: false,
        error: 'An account with this email already exists. Please sign in instead.',
      };
    }

    // Credential already linked (they already upgraded)
    if (code === 'auth/provider-already-linked') {
      return { success: true, uid: user.uid, email, isNewAccount: false };
    }

    // Weak password
    if (code === 'auth/weak-password') {
      return {
        success: false,
        uid: user.uid,
        email,
        isNewAccount: false,
        error: 'Password must be at least 6 characters.',
      };
    }

    // Invalid email
    if (code === 'auth/invalid-email') {
      return {
        success: false,
        uid: user.uid,
        email,
        isNewAccount: false,
        error: 'Please enter a valid email address.',
      };
    }

    return {
      success: false,
      uid: user.uid,
      email,
      isNewAccount: false,
      error: message || 'Account creation failed. Please try again.',
    };
  }
}

// ── Sign in returning patient ──

/**
 * Sign in an existing patient with email/password.
 * This replaces the current anonymous auth session.
 */
export async function signInPatient(
  email: string,
  password: string,
): Promise<AuthUpgradeResult> {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    // Reset cached UID so getAuthUid() resolves to the new user
    resetAuthUid();
    // Rotate session on explicit login
    onLogin();
    return { success: true, uid: result.user.uid, email, isNewAccount: false };
  } catch (err: unknown) {
    const code = (err as { code?: string }).code;

    if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') {
      return { success: false, uid: '', email, isNewAccount: false, error: 'Invalid email or password.' };
    }
    if (code === 'auth/user-not-found') {
      return { success: false, uid: '', email, isNewAccount: false, error: 'No account found with this email.' };
    }
    if (code === 'auth/too-many-requests') {
      return { success: false, uid: '', email, isNewAccount: false, error: 'Too many attempts. Please try again later.' };
    }
    if (code === 'auth/invalid-email') {
      return { success: false, uid: '', email, isNewAccount: false, error: 'Please enter a valid email address.' };
    }

    return {
      success: false,
      uid: '',
      email,
      isNewAccount: false,
      error: (err as Error).message || 'Sign in failed. Please try again.',
    };
  }
}

// ── Create new patient account (non-anonymous start) ──

/**
 * Create a brand new email/password account for a patient
 * who navigates directly to login/register (not upgrading anonymous).
 */
export async function createPatientAccount(
  email: string,
  password: string,
): Promise<AuthUpgradeResult> {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    // Reset cached UID so getAuthUid() resolves to the new user
    resetAuthUid();

    // Fire-and-forget: persist email to user_profiles
    persistEmailToProfile(result.user.uid, email).catch((err) =>
      console.warn('[AuthUpgrade] Profile email write failed:', err),
    );

    return { success: true, uid: result.user.uid, email, isNewAccount: true };
  } catch (err: unknown) {
    const code = (err as { code?: string }).code;

    if (code === 'auth/email-already-in-use') {
      return {
        success: false,
        uid: '',
        email,
        isNewAccount: false,
        error: 'An account with this email already exists. Please sign in instead.',
      };
    }
    if (code === 'auth/weak-password') {
      return {
        success: false,
        uid: '',
        email,
        isNewAccount: false,
        error: 'Password must be at least 6 characters.',
      };
    }
    if (code === 'auth/invalid-email') {
      return {
        success: false,
        uid: '',
        email,
        isNewAccount: false,
        error: 'Please enter a valid email address.',
      };
    }

    return {
      success: false,
      uid: '',
      email,
      isNewAccount: false,
      error: (err as Error).message || 'Account creation failed. Please try again.',
    };
  }
}

// ── Password reset ──

export async function sendPatientPasswordReset(email: string): Promise<{ success: boolean; error?: string }> {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (err: unknown) {
    const code = (err as { code?: string }).code;
    if (code === 'auth/user-not-found') {
      // Don't reveal whether email exists (security best practice)
      return { success: true };
    }
    return { success: false, error: 'Failed to send reset email. Please try again.' };
  }
}

// ── Auth state listener ──

/**
 * Subscribe to patient auth state changes.
 * Returns an unsubscribe function.
 */
export function onPatientAuthStateChanged(
  callback: (state: PatientAuthState) => void,
): () => void {
  return onAuthStateChanged(auth, (user: User | null) => {
    if (user) {
      callback({
        uid: user.uid,
        email: user.email,
        isAnonymous: user.isAnonymous,
        isAuthenticated: !user.isAnonymous && !!user.email,
      });
    } else {
      callback({
        uid: null,
        email: null,
        isAnonymous: true,
        isAuthenticated: false,
      });
    }
  });
}

/**
 * Get current patient auth state (synchronous snapshot).
 */
export function getCurrentPatientAuth(): PatientAuthState {
  const user = auth.currentUser;
  if (!user) {
    return { uid: null, email: null, isAnonymous: true, isAuthenticated: false };
  }
  return {
    uid: user.uid,
    email: user.email,
    isAnonymous: user.isAnonymous,
    isAuthenticated: !user.isAnonymous && !!user.email,
  };
}

// ── Fetch patient journeys by UID ──

export interface PatientJourneySummary {
  sessionId: string;
  createdAt: string;
  lastActivityAt: string;
  status: string;
  currentStep: string;
  protocolId: string | null;
  labReportId: string | null;
  triageData: {
    sex: string;
    age: number;
  } | null;
}

/**
 * Fetch all journeys for a given UID, ordered by creation date descending.
 * Used by the My Results dashboard.
 */
export async function getPatientJourneys(uid: string): Promise<PatientJourneySummary[]> {
  try {
    const q = query(
      collection(db, C_PATIENT_JOURNEYS),
      where('uid', '==', uid),
      orderBy('_createdAt', 'desc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        sessionId: data.sessionId ?? d.id,
        createdAt: data.createdAt ?? '',
        lastActivityAt: data.lastActivityAt ?? '',
        status: data.status ?? 'unknown',
        currentStep: data.currentStep ?? 'unknown',
        protocolId: data.protocolId ?? null,
        labReportId: data.labReportId ?? null,
        triageData: data.triageData ?? null,
      };
    });
  } catch (err) {
    console.warn('[AuthUpgrade] Failed to fetch patient journeys:', err);
    return [];
  }
}

// ── Private helpers ──

/** Persist email to user_profiles collection. Fire-and-forget. */
async function persistEmailToProfile(uid: string, email: string): Promise<void> {
  await setDoc(
    doc(db, C_USER_PROFILES, uid),
    {
      uid,
      email,
      emailLinkedAt: Timestamp.now(),
      _updatedAt: Timestamp.now(),
    },
    { merge: true },
  );
}
